import { useCallback, useEffect, useMemo, useState } from "react"
import { Heading } from "@/components/ui/typography"
import { useNavigate } from "react-router-dom"
import { ExternalLink, Shield } from "lucide-react"
import { useTranslation } from "react-i18next"

import { KycEmbed } from "@/components/kyc/KycEmbed"
import { Button } from "@/components/ui/button"
import { toast } from "@/hooks/use-toast"
import { track } from "@/lib/analytics/track"
import { getPendingKycLink } from "@/lib/onboarding/pendingKycState"
import { EmptyState, Page, PageToolbar } from "@supabank/ui"
import type { PageToolbarProps } from "@supabank/ui"

export default function ToSWaitingPage() {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const [tosLink, setTosLink] = useState<string | null>(null)

  const toolbarItems = useMemo<PageToolbarProps["items"]>(
    () => [
      { id: "search", hotkey: "K", ariaLabel: "Search help", placeholder: "Search help" },
      { id: "refresh", hotkey: "R", ariaLabel: "Reload ToS" },
      { id: "filters", hotkey: "F", ariaLabel: "Edit profile" },
      { id: "export", hotkey: "E", ariaLabel: "Open ToS externally" },
      { id: "primary", hotkey: "N", ariaLabel: "Return to profile", label: "Back to Profile" },
    ],
    [],
  )

  const handleToolbarAction: PageToolbarProps["onAction"] = (itemId) => {
    switch (itemId) {
      case "refresh":
        window.location.reload()
        break
      case "filters":
      case "primary":
        navigate("/onboarding/profile")
        break
      case "export":
        if (tosLink) {
          window.open(tosLink, "_blank", "noopener,noreferrer")
        }
        break
      default:
        break
    }
  }

  useEffect(() => {
    const loadTosLink = async () => {
      const pendingKyc = getPendingKycLink()
      if (!pendingKyc) {
        track("tos_waiting_no_pending_kyc")
        toast({
          title: "Please Start Verification",
          description: "Redirecting you to begin the verification process...",
        })
        navigate("/onboarding/wizard", { replace: true })
        return
      }

      const storedTosLink = localStorage.getItem("supabank:pending_tos_link")
      if (storedTosLink) {
        setTosLink(storedTosLink)
        track("tos_waiting_page_loaded", { tosLink: storedTosLink })
      } else {
        track("tos_waiting_missing_link")
        toast({
          title: "Redirecting",
          description: "Loading verification page...",
        })
        navigate("/onboarding/wizard", { replace: true })
      }
    }

    loadTosLink()
  }, [navigate])

  const handleTosComplete = useCallback(() => {
    track("tos_accepted_postmessage")
    const pendingKyc = getPendingKycLink()
    if (!pendingKyc?.kycLink) {
      toast({
        title: "Error",
        description: "Verification link not found. Please try again.",
        variant: "destructive",
      })
      navigate("/onboarding/profile", { replace: true })
      return
    }

    toast({
      title: "Terms Accepted!",
      description: "Launching identity verification...",
    })

    setTimeout(() => {
      localStorage.removeItem("supabank:pending_tos_link")
      localStorage.removeItem("supabank:pending_kyc_link")
      localStorage.removeItem("supabank:pending_kyc_timestamp")
      window.location.assign(pendingKyc.kycLink)
    }, 500)
  }, [navigate])

  const handleIframeError = useCallback((errorMessage: string) => {
    track("tos_iframe_error", { error: errorMessage })
  }, [])

  const handleCancel = useCallback(() => {
    track("tos_waiting_cancelled")
    navigate("/onboarding/profile", { replace: true })
  }, [navigate])

  const body = tosLink ? (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-8">
      <div className="space-y-2">
        <Heading level={2}>{t('errors.tosWaiting.title')}</Heading>
        <p className="text-sm text-muted-foreground">
          {t('errors.tosWaiting.description')}
        </p>
      </div>

      <KycEmbed
        url={tosLink}
        allowedHosts={["bridge.xyz", "dashboard.bridge.xyz"]}
        onComplete={handleTosComplete}
        onError={handleIframeError}
        title={t('errors.tosWaiting.termsTitle')}
        description={t('errors.tosWaiting.termsDescription')}
        showFallback
      />

      <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
        <p className="mb-2 font-medium text-foreground">{t('errors.tosWaiting.whatHappensNext')}</p>
        <ol className="list-inside list-decimal space-y-1">
          <li>{t('errors.tosWaiting.step1')}</li>
          <li>{t('errors.tosWaiting.step2')}</li>
          <li>{t('errors.tosWaiting.step3')}</li>
          <li>{t('errors.tosWaiting.step4')}</li>
        </ol>
        <div className="mt-4 border-t border-border pt-4">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            {t('errors.tosWaiting.cancel')}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <EmptyState
      icon={ExternalLink}
      title={t('errors.tosWaiting.loadingTitle')}
      description={t('errors.tosWaiting.loadingDescription')}
      action={{
        label: t('errors.tosWaiting.returnToProfile'),
        onClick: handleCancel,
      }}
    />
  )

  return (
    <Page data-ux-contract="canon">
      <PageHeader
        icon={Shield}
        title={t('errors.tosWaiting.pageTitle')}
        subtitle={t('errors.tosWaiting.pageSubtitle')}
        badge="KYC"
      />
      <Page.Toolbar items={toolbarItems} onAction={handleToolbarAction} />
      <PageContent className="py-10">{body}</PageContent>
    </Page>
  )
}
